// Hubstaff plugins
import AppMessagesPlugin from './app_messages';
import FiltersPlugin from './filters';
import { attachErrorReporterToVueApp } from '~/utils/errorReporter';

import { ValidationObserver, ValidationProvider, configure, extend } from '~/utils/vee_validate_compat';
import { required, integer, numeric, max_value, between } from '@vee-validate/rules';

// Global components
import HubAvatar from '~/components/HubAvatar.vue';
import AppUpsellBanner from '~/components/AppUpsellBanner.vue';
import AppAvatar from '~/components/AppAvatar.vue';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import AppAchievementBadge from '~/components/AppAchievementBadge.vue';
import AppAchievementBadges from '~/components/AppAchievementBadges.vue';
import AppBatchActionsPanel from '~/components/AppBatchActionsPanel.vue';
import AppBlacklistedMessage from '~/components/AppBlacklistedMessage.vue';
import AppButton from '~/components/AppButton.vue';
import AppChartDonut from '~/components/AppChartDonut.vue';
import AppChartLine from '~/components/AppChartLine.vue';
import AppCheckbox from '~/components/AppCheckbox.vue';
import AppColumnToggle from '~/components/AppColumnToggle.vue';
import AppConfirm from '~/components/AppConfirm.vue';
import AppCopyInputValue from '~/components/AppCopyInputValue.vue';
import AppDatePicker from '~/components/AppDatePicker.vue';
import AppDateRangePicker from '~/components/AppDateRangePicker.vue';
import AppDatetimeRangePicker from '~/components/AppDatetimeRangePicker.vue';
import AppTooltipIcon from '~/components/AppTooltipIcon.vue';
import AppDialog from '~/components/AppDialog.vue';
import AppDialogError from '~/components/AppDialogError.vue';
import AppDropdown from '~/components/AppDropdown.vue';
import AppDropdownItem from '~/components/AppDropdownItem.vue';
import AppDropdownItemGroup from '~/components/AppDropdownItemGroup.vue';
import AppToggleDropdown from '~/components/AppToggleDropdown.vue';
import AppToggleDropdownItem from '~/components/AppToggleDropdownItem.vue';
import AppDurationInput from '~/components/AppDurationInput.vue';
import AppExcerpt from '~/components/AppExcerpt.vue';
import AppExportMenu from '~/components/AppExportMenu.vue';
import AppExportMenuItem from '~/components/AppExportMenuItem.vue';
import AppFileImport from '~/components/AppFileImport.vue';
import AppFilePicker from '~/components/AppFilePicker.vue';
import AppFilterCount from '~/components/AppFilterCount.vue';
import AppGallery from '~/components/AppGallery.vue';
import AppGalleryItem from '~/components/AppGalleryItem.vue';
import AppHintPopover from '~/components/AppHintPopover.vue';
import AppHoverLink from '~/components/AppHoverLink.vue';
import AppImageUploader from '~/components/AppImageUploader.vue';
import AppInfiniteScroll from '~/components/AppInfiniteScroll.vue';
import AppInput from '~/components/AppInput.vue';
import AppTimeSelect from '~/components/AppTimeSelect.vue';
import AppTimeInput from '~/components/AppTimeInput.vue';
import AppTimeGrid from '~/components/AppTimeGrid.vue';
import AppTimePicker from '~/components/AppTimePicker.vue';
import AppTimeZoneDropdown from '~/components/AppTimeZoneDropdown.vue';
import AppTimeGridHeaderTableData from '~/components/AppTimeGridHeaderTableData.vue';
import AppInputSearch from '~/components/AppInputSearch.vue';
import AppIntervalDayPicker from '~/components/AppIntervalDayPicker.vue';
import AppYearPicker from '~/components/AppYearPicker.vue';
import AppLink from '~/components/AppLink.vue';
import AppTableLoading from '~/components/AppTableLoading.vue';
import AppMap from '~/components/AppMap.vue';
import AppMapCluster from '~/components/AppMapCluster.vue';
import AppMapDropdownButton from '~/components/AppMapDropdownButton.vue';
import AppMapGeolocationControl from '~/components/AppMapGeolocationControl.vue';
import AppMapMarker from '~/components/AppMapMarker.vue';
import AppMarkdown from '~/components/AppMarkdown.vue';
import AppNav from '~/components/AppNav.vue';
import AppOnOffToggle from '~/components/AppOnOffToggle.vue';
import AppToggleButton from '~/components/AppToggleButton.vue';
import AppToggleButtonGroup from '~/components/AppToggleButtonGroup.vue';
import AppPagination from '~/components/AppPagination.vue';
import AppPopover from '~/components/AppPopover.vue';
// Vue 3 port of vue-popperjs (Vue-2-only lib), same global name as the Vue 2 hub_app.
import VuePopper from '~/components/VuePopper.vue';
import AppProgressBar from '~/components/AppProgressBar.vue';
import AppRadioButton from '~/components/AppRadioButton.vue';
import AppRadioButtonGroup from '~/components/AppRadioButtonGroup.vue';
import AppRangeSlider from '~/components/AppRangeSlider.vue';
import AppScrollableTable from '~/components/AppScrollableTable.vue';
import AppSelect from '~/components/AppSelect.vue';
import AppSelectDropdown from '~/components/AppSelectDropdown.vue';
import AppSelectList from '~/components/AppSelectList.vue';
import AppLazySelect from '~/components/AppLazySelect.vue';
import AppLazySelectItem from '~/components/AppLazySelectItem.vue';
import AppMembersSelector from '~/components/AppMembersSelector.vue';
import AppMembersSelection from '~/components/AppMembersSelection.vue';
import AppSelectListItem from '~/components/AppSelectListItem.vue';
import AppSettingsLinkDropdown from '~/components/AppSettingsLinkDropdown.vue';
import AppSidebar from '~/components/AppSidebar.vue';
import AppSwitch from '~/components/AppSwitch.vue';
import AppTab from '~/components/AppTab.vue';
import AppTabs from '~/components/AppTabs.vue';
import AppTable from '~/components/AppTable.vue';
import AppTableColumn from '~/components/AppTableColumn.vue';
import AppTasksAlert from '~/components/AppTasksAlert.vue';
import AppWizard from '~/components/AppWizard.vue';
import AppWizardStep from '~/components/AppWizardStep.vue';
import AppAlert from '~/components/AppAlert.vue';
import AppVerticalNav from '~/components/AppVerticalNav.vue';

// Global directives
import newTooltipDirective from '../directives/new-tooltip';
import tooltipDirective from '../directives/tooltip';
import popoverDirective from '../directives/popover';
import loadingDirective from '../directives/loading';
import trackDirective from '../directives/track';

extend('required', {
  ...required,
  message: "can't be blank",
});

extend('integer', {
  ...integer,
  message: 'enter a valid number',
});

extend('numeric', {
  ...numeric,
  message: 'enter a positive number',
});

extend('max_value', {
  ...max_value,
  message: 'enter a smaller number',
});

extend('between', {
  params: ['min', 'max'],
  ...between,
  message: 'enter a number between {min} and {max}',
});

// Define validation rules for vee-validate v4
configure({
  // Global configuration
  validateOnBlur: true,
  validateOnChange: true,
  validateOnInput: false,
  validateOnModelUpdate: true,
});

export default {
  install(app) {
    attachErrorReporterToVueApp(app);

    // The SPA mounts on an element pre-tagged with `.loading-page` (a spinner + forced-height
    // placeholder shown until the bundle boots). We're booting now, so clear it — Vue 3 mounts
    // INTO the element and keeps its classes (unlike Vue 2's element-replacing `$mount`), so it
    // would otherwise linger over the mounted app.
    document.querySelectorAll('.loading-page').forEach((el) => el.classList.remove('loading-page'));

    // Plugins
    app.use(AppMessagesPlugin);
    app.use(FiltersPlugin);
    app.component('ValidationObserver', ValidationObserver);
    app.component('ValidationProvider', ValidationProvider);
    // App[Components]
    app.component('HubAvatar', HubAvatar);
    app.component('AppAvatar', AppAvatar);
    app.component('AppUpsellBanner', AppUpsellBanner);
    app.component('AppAvatarInitial', AppAvatarInitial);
    app.component('AppAchievementBadge', AppAchievementBadge);
    app.component('AppAchievementBadges', AppAchievementBadges);
    app.component('AppBatchActionsPanel', AppBatchActionsPanel);
    app.component('AppBlacklistedMessage', AppBlacklistedMessage);
    app.component('AppButton', AppButton);
    app.component('AppChartDonut', AppChartDonut);
    app.component('AppChartLine', AppChartLine);
    app.component('AppCheckbox', AppCheckbox);
    app.component('AppColumnToggle', AppColumnToggle);
    app.component('AppConfirm', AppConfirm);
    app.component('AppCopyInputValue', AppCopyInputValue);
    app.component('AppDatePicker', AppDatePicker);
    app.component('AppDateRangePicker', AppDateRangePicker);
    app.component('AppDatetimeRangePicker', AppDatetimeRangePicker);
    app.component('AppTooltipIcon', AppTooltipIcon);
    app.component('AppDialog', AppDialog);
    app.component('AppDialogError', AppDialogError);
    app.component('AppDropdown', AppDropdown);
    app.component('AppDropdownItem', AppDropdownItem);
    app.component('AppDropdownItemGroup', AppDropdownItemGroup);
    app.component('AppToggleDropdown', AppToggleDropdown);
    app.component('AppToggleDropdownItem', AppToggleDropdownItem);
    app.component('AppDurationInput', AppDurationInput);
    app.component('AppExcerpt', AppExcerpt);
    app.component('AppExportMenu', AppExportMenu);
    app.component('AppExportMenuItem', AppExportMenuItem);
    app.component('AppFileImport', AppFileImport);
    app.component('AppFilePicker', AppFilePicker);
    app.component('AppFilterCount', AppFilterCount);
    app.component('AppGallery', AppGallery);
    app.component('AppGalleryItem', AppGalleryItem);
    app.component('AppHintPopover', AppHintPopover);
    app.component('AppHoverLink', AppHoverLink);
    app.component('AppImageUploader', AppImageUploader);
    app.component('AppInfiniteScroll', AppInfiniteScroll);
    app.component('AppInput', AppInput);
    app.component('AppTimeInput', AppTimeInput);
    app.component('AppTimeSelect', AppTimeSelect);
    app.component('AppTimePicker', AppTimePicker);
    app.component('AppTimeGrid', AppTimeGrid);
    app.component('AppTimeZoneDropdown', AppTimeZoneDropdown);
    app.component('AppTimeGridHeaderTableData', AppTimeGridHeaderTableData);
    app.component('AppInputSearch', AppInputSearch);
    app.component('AppIntervalDayPicker', AppIntervalDayPicker);
    app.component('AppYearPicker', AppYearPicker);
    app.component('AppLink', AppLink);
    app.component('AppTableLoading', AppTableLoading);
    app.component('AppMap', AppMap);
    app.component('AppMapCluster', AppMapCluster);
    app.component('AppMapDropdownButton', AppMapDropdownButton);
    app.component('AppMapGeolocationControl', AppMapGeolocationControl);
    app.component('AppMapMarker', AppMapMarker);
    app.component('AppMarkdown', AppMarkdown);
    app.component('AppNav', AppNav);
    app.component('AppOnOffToggle', AppOnOffToggle);
    app.component('AppToggleButton', AppToggleButton);
    app.component('AppToggleButtonGroup', AppToggleButtonGroup);
    app.component('AppPagination', AppPagination);
    app.component('AppPopover', AppPopover);
    app.component('VuePopper', VuePopper);
    app.component('AppProgressBar', AppProgressBar);
    app.component('AppRadioButton', AppRadioButton);
    app.component('AppRadioButtonGroup', AppRadioButtonGroup);
    app.component('AppRangeSlider', AppRangeSlider);
    app.component('AppScrollableTable', AppScrollableTable);
    app.component('AppSelect', AppSelect);
    app.component('AppSelectDropdown', AppSelectDropdown);
    app.component('AppSelectList', AppSelectList);
    app.component('AppMembersSelector', AppMembersSelector);
    app.component('AppMembersSelection', AppMembersSelection);

    app.component('AppLazySelect', AppLazySelect);
    app.component('AppLazySelectItem', AppLazySelectItem);

    app.component('AppSelectListItem', AppSelectListItem);
    app.component('AppSettingsLinkDropdown', AppSettingsLinkDropdown);
    app.component('AppSidebar', AppSidebar);
    app.component('AppSwitch', AppSwitch);
    app.component('AppTab', AppTab);
    app.component('AppTabs', AppTabs);
    app.component('AppTable', AppTable);
    app.component('AppTableColumn', AppTableColumn);
    app.component('AppTasksAlert', AppTasksAlert);
    app.component('AppWizard', AppWizard);
    app.component('AppWizardStep', AppWizardStep);
    app.component('AppAlert', AppAlert);
    app.component('AppVerticalNav', AppVerticalNav);

    // Directives
    app.directive('ntooltip', newTooltipDirective);
    app.directive('tooltip', tooltipDirective);
    app.directive('popover', popoverDirective);
    app.directive('loading', loadingDirective);
    app.directive('track', trackDirective);
  },
};
